import { useState, type FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { CONCEPTOS_PAGO_EMPLEADO } from '../../types/domain';
import type { PagoEmpleadoConProfesional } from '../../types/domain';
import type { PagoEmpleadoRow, ProfesionalRow } from '../../types/database.types';

const COP = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

function periodoActual(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const ESTADO_BADGE: Record<string, string> = {
  pendiente: 'bg-amber-100 text-amber-800',
  liquidado: 'bg-blue-100 text-blue-800',
  pagado: 'bg-green-100 text-green-800',
  anulado: 'bg-red-100 text-red-700',
};

/**
 * Pagos a empleados (DAF v2, sección 23): salario, comisión,
 * bonificación, anticipo, descuento — con estados pendiente/liquidado/
 * pagado/anulado. Escritura admin-only, igual que el resto de módulos
 * financieros. Marcar como "pagado" pasa por el RPC `pagar_empleado`
 * (supabase/schema_comisiones_pagos.sql), que además genera el egreso
 * correspondiente en `movimientos_caja` si hay una caja abierta.
 */
export function PagosEmpleados() {
  const { perfil } = useAuth();
  const [mostrarForm, setMostrarForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: profesionales } = useSupabaseQuery<ProfesionalRow[]>(async () =>
    supabase.from('profesionales').select('*').eq('estado', 'activo').order('nombre'),
  );

  const { data, loading, refetch } = useSupabaseQuery<PagoEmpleadoConProfesional[]>(async () => {
    const { data, error } = await supabase
      .from('pagos_empleados')
      .select('*, profesionales ( nombre )')
      .order('fecha', { ascending: false })
      .limit(100);

    if (error) return { data: null, error };

    const items = (data as unknown as Array<Record<string, unknown>>).map((row) => ({
      ...(row as unknown as PagoEmpleadoConProfesional),
      profesional_nombre: (row.profesionales as { nombre: string } | null)?.nombre ?? '—',
    }));

    return { data: items, error: null };
  });

  function alCambiar() {
    setError(null);
    refetch();
  }

  async function marcarLiquidado(id: string) {
    setError(null);
    const { error: err } = await supabase.from('pagos_empleados').update({ estado: 'liquidado' }).eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    refetch();
  }

  async function marcarPagado(id: string) {
    setError(null);
    const { error: err } = await supabase.rpc('pagar_empleado', { p_pago_id: id });
    if (err) {
      setError(err.message);
      return;
    }
    refetch();
  }

  async function anular(id: string) {
    setError(null);
    const { error: err } = await supabase.from('pagos_empleados').update({ estado: 'anulado' }).eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    refetch();
  }

  return (
    <div>
      <PageHeader
        title="Pagos a empleados"
        description="Salarios, comisiones, bonificaciones, anticipos y descuentos — DAF v2, sección 23"
        actions={
          <button className="btn-primary" onClick={() => setMostrarForm((v) => !v)}>
            {mostrarForm ? 'Cancelar' : 'Nuevo pago'}
          </button>
        }
      />

      {mostrarForm && perfil && (
        <NuevoPagoForm
          salonId={perfil.salon_id}
          profesionales={profesionales ?? []}
          onGuardado={() => {
            setMostrarForm(false);
            alCambiar();
          }}
          onCancelar={() => setMostrarForm(false)}
        />
      )}

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}
      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}

      {data && data.length === 0 && <EmptyState title="Sin pagos registrados todavía" />}

      {data && data.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-onyx/10 text-left text-onyx/50">
                <th className="px-4 py-3 font-medium">Profesional</th>
                <th className="px-4 py-3 font-medium">Concepto</th>
                <th className="px-4 py-3 font-medium">Período</th>
                <th className="px-4 py-3 font-medium">Monto</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.map((p) => (
                <tr key={p.id} className="border-b border-onyx/5 last:border-0">
                  <td className="px-4 py-3 text-onyx">{p.profesional_nombre}</td>
                  <td className="px-4 py-3 text-onyx/70">{CONCEPTOS_PAGO_EMPLEADO.find((c) => c.value === p.concepto)?.label ?? p.concepto}</td>
                  <td className="px-4 py-3 text-onyx/70">{p.periodo}</td>
                  <td className="px-4 py-3 font-medium text-onyx">{COP.format(p.monto)}</td>
                  <td className="px-4 py-3">
                    <span className={`badge ${ESTADO_BADGE[p.estado] ?? 'bg-gray-200 text-gray-700'}`}>{p.estado}</span>
                  </td>
                  <td className="px-4 py-3 text-right text-xs">
                    {p.estado === 'pendiente' && (
                      <button className="mr-3 text-gold-deep hover:underline" onClick={() => marcarLiquidado(p.id)}>
                        Liquidar
                      </button>
                    )}
                    {(p.estado === 'pendiente' || p.estado === 'liquidado') && (
                      <button className="mr-3 text-green-700 hover:underline" onClick={() => marcarPagado(p.id)}>
                        Marcar pagado
                      </button>
                    )}
                    {p.estado !== 'pagado' && p.estado !== 'anulado' && (
                      <button className="text-red-600 hover:underline" onClick={() => anular(p.id)}>
                        Anular
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NuevoPagoForm({
  salonId,
  profesionales,
  onGuardado,
  onCancelar,
}: {
  salonId: string;
  profesionales: ProfesionalRow[];
  onGuardado: () => void;
  onCancelar: () => void;
}) {
  const [profesionalId, setProfesionalId] = useState('');
  const [concepto, setConcepto] = useState<PagoEmpleadoRow['concepto']>('salario');
  const [monto, setMonto] = useState('');
  const [periodo, setPeriodo] = useState(periodoActual());
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const montoNum = Number(monto);
    if (!profesionalId || !montoNum || montoNum <= 0) {
      setError('Selecciona una profesional y un monto mayor que cero');
      return;
    }

    setGuardando(true);
    const { error: err } = await supabase.from('pagos_empleados').insert({
      salon_id: salonId,
      profesional_id: profesionalId,
      concepto,
      monto: montoNum,
      periodo,
      estado: 'pendiente',
    });
    setGuardando(false);

    if (err) {
      setError(err.message);
      return;
    }

    onGuardado();
  }

  return (
    <form onSubmit={handleSubmit} className="card mb-4 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Profesional *</span>
          <select required className="input-field" value={profesionalId} onChange={(e) => setProfesionalId(e.target.value)}>
            <option value="" disabled>
              Selecciona una profesional
            </option>
            {profesionales.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Concepto *</span>
          <select className="input-field" value={concepto} onChange={(e) => setConcepto(e.target.value as typeof concepto)}>
            {CONCEPTOS_PAGO_EMPLEADO.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Monto *</span>
          <input required type="number" min={1} step="0.01" className="input-field" value={monto} onChange={(e) => setMonto(e.target.value)} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Período *</span>
          <input type="month" className="input-field" value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
        </label>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-3">
        <button type="submit" disabled={guardando} className="btn-primary">
          {guardando ? 'Guardando…' : 'Registrar pago'}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
